/**
 * FindingsTable — sortable table of detected security findings.
 */

import { useState } from 'react'
import type { Finding } from '../types/scan'
import { ChevronDown, ChevronUp, AlertTriangle, Info } from 'lucide-react'
import styles from './FindingsTable.module.css'

interface Props {
  findings: Finding[]
}

type SortField = 'severity' | 'className' | 'category'

const SEVERITY_RANK = { critical: 4, high: 3, medium: 2, low: 1, info: 0 }

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high:     '#ef4444',
  medium:   '#f59e0b',
  low:      '#84cc16',
  info:     '#60a5fa',
}

const SEVERITY_BG: Record<string, string> = {
  critical: 'rgba(220, 38, 38, 0.12)',
  high:     'rgba(239, 68, 68, 0.1)',
  medium:   'rgba(245, 158, 11, 0.1)',
  low:      'rgba(132, 204, 22, 0.1)',
  info:     'rgba(96, 165, 250, 0.1)',
}

export default function FindingsTable({ findings }: Props) {
  const [sortField, setSortField] = useState<SortField>('severity')
  const [sortAsc, setSortAsc] = useState(false)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  const sorted = [...findings].sort((a, b) => {
    let cmp = 0
    if (sortField === 'severity') {
      cmp = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
    } else if (sortField === 'className') {
      cmp = a.className.localeCompare(b.className)
    } else if (sortField === 'category') {
      cmp = a.category.localeCompare(b.category)
    }
    return sortAsc ? cmp : -cmp
  })

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc((v) => !v)
    } else {
      setSortField(field)
      setSortAsc(false)
    }
  }

  const toggleExpand = (idx: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  if (findings.length === 0) {
    return (
      <div className={styles.empty}>
        <Info size={20} />
        <span>Nie wykryto żadnych podejrzanych wzorców</span>
      </div>
    )
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown size={14} className={styles.sortIconInactive} />
    return sortAsc ? <ChevronUp size={14} /> : <ChevronDown size={14} />
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          <AlertTriangle size={18} />
          Znaleziska
          <span className={styles.count}>{findings.length}</span>
        </h2>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th} onClick={() => toggleSort('severity')}
                aria-sort={sortField === 'severity' ? (sortAsc ? 'ascending' : 'descending') : 'none'}>
                <span className={styles.thInner}>Powaga <SortIcon field="severity" /></span>
              </th>
              <th className={styles.th} onClick={() => toggleSort('className')}
                aria-sort={sortField === 'className' ? (sortAsc ? 'ascending' : 'descending') : 'none'}>
                <span className={styles.thInner}>Klasa <SortIcon field="className" /></span>
              </th>
              <th className={styles.th} onClick={() => toggleSort('category')}
                aria-sort={sortField === 'category' ? (sortAsc ? 'ascending' : 'descending') : 'none'}>
                <span className={styles.thInner}>Kategoria <SortIcon field="category" /></span>
              </th>
              <th className={styles.th}>Opis</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((f, idx) => {
              const isExpanded = expanded.has(idx)
              return (
                <>
                  <tr
                    key={`row-${idx}`}
                    className={styles.tr}
                    onClick={() => toggleExpand(idx)}
                    style={{ cursor: f.codeSnippet ? 'pointer' : 'default' }}
                    aria-expanded={isExpanded}
                  >
                    <td className={styles.td}>
                      <span
                        className={styles.severityBadge}
                        style={{
                          color: SEVERITY_COLORS[f.severity],
                          background: SEVERITY_BG[f.severity],
                          borderColor: SEVERITY_COLORS[f.severity],
                        }}
                      >
                        {f.severity}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.classCell}>
                        <code className={styles.className}>{f.className}</code>
                        {f.methodName && (
                          <code className={styles.methodName}>.{f.methodName}</code>
                        )}
                        {f.lineNumber && (
                          <span className={styles.lineNum}>:{f.lineNumber}</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.categoryTag}>
                        {f.category.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.description}>{f.description}</span>
                      {f.codeSnippet && (
                        <span className={styles.expandHint}>
                          {isExpanded ? '▲ ukryj' : '▼ kod'}
                        </span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && f.codeSnippet && (
                    <tr key={`snippet-${idx}`} className={styles.snippetRow}>
                      <td colSpan={4} className={styles.snippetCell}>
                        <pre className={styles.snippet}>{f.codeSnippet}</pre>
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
